import { useGetIsQpv } from "@api";
import type { IAddress, TAddressQPV } from "@bbj-types/address";
import { CheckIcon } from "@components/navigations/Stepper/styles";
import { type AutocompleteChangeReason, CircularProgress, Stack, Typography } from "@mui/material";
import type React from "react";
import { useEffect } from "react";
import { Address, type IAddressProps, type TAddressTextFieldProps } from "../Address";

export interface IAddressQPVProps<DisableClearable extends boolean | undefined>
  extends Omit<IAddressProps<DisableClearable>, "TextFieldProps" | "value" | "onChange"> {
  onChange?: (
    event: React.SyntheticEvent<Element, Event> | null,
    value: TAddressQPV | null,
    reason: AutocompleteChangeReason
  ) => void;
  TextFieldProps?: TAddressTextFieldProps;
  value: (DisableClearable extends true ? TAddressQPV : TAddressQPV | null) | undefined;
}

export function AddressQPV<DisableClearable extends boolean | undefined = undefined>({
  value,
  onChange,
  TextFieldProps,
  ...props
}: IAddressQPVProps<DisableClearable>) {
  const [{ data: resultIsQpv, loading: loadingResultIsQpv }, getIsQpv] = useGetIsQpv();

  //set QPV status to selected address
  useEffect(() => {
    if (value && resultIsQpv && onChange) {
      onChange(null, { ...value, ...resultIsQpv }, "selectOption");
    }
  }, [resultIsQpv]);

  const handleChange = async (
    event: React.SyntheticEvent<Element, Event>,
    newValue: DisableClearable extends true ? IAddress : IAddress | null,
    reason: AutocompleteChangeReason
  ) => {
    if (newValue) {
      await getIsQpv(newValue.loc);
    }
    if (event.type !== "blur" && onChange) {
      const tmpValue = newValue ? { ...newValue, isQPV: false } : null;
      onChange(event, tmpValue, reason);
    }
  };

  const QPVDisplay = (
    <>
      {(value?.isQPV !== undefined || loadingResultIsQpv) && (
        <Stack
          component="span"
          direction="row"
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {loadingResultIsQpv ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <Typography component="span" variant="body1" color="textSecondary">
                {value?.isQPV ? `Adresse QPV${value?.QPVName ? ` : ${value.QPVName}` : ""}` : "Adresse Non QPV"}
              </Typography>
              {value?.isQPV && <CheckIcon fontSize="small" sx={{ color: (theme) => theme.palette.success.main }} />}
            </>
          )}
        </Stack>
      )}
    </>
  );

  return (
    <Address
      value={value}
      onChange={handleChange}
      TextFieldProps={{ helperText: QPVDisplay, ...TextFieldProps }}
      {...props}
    />
  );
}
